import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, DatePicker, Drawer, Empty, Segmented, Select, Space, message, theme } from 'antd';
import { RightOutlined, WarningFilled } from '@ant-design/icons';
import dayjs from 'libs/dayjs';
import GanttBars from './GanttBars';
import { avisosDeTarea, columnaInicial, rangoDelDiagrama } from './ganttGeometry';
import { agruparTareasMovil, miniBarra, tareasConBarra } from './ganttMobile';

const COL_WIDTH_MOVIL = 28;
const MARGEN_HOY = 60;

const fechaCorta = (iso) => (iso ? dayjs(iso).format('DD/MM') : '—');
const duracion = (t) => {
  if (!t.planned_start || !t.planned_end) return null;
  const dias = dayjs(t.planned_end).diff(dayjs(t.planned_start), 'day') + 1;
  return dias === 1 ? '1 día' : `${dias} días`;
};

// Tarjeta de una tarea: nombre (con su padre si es subtarea), responsable, rango, duración
// y una mini-barra proporcional al rango de la ODT.
function TarjetaTarea({ tarea, padre, avisos, rango, nombreResponsable, onAbrir, disabled }) {
  const { token } = theme.useToken();
  const barra = miniBarra(tarea, rango);
  const color = avisos.length > 0 ? token.colorError : token.colorPrimary;
  const dur = duracion(tarea);

  return (
    <button
      type="button"
      data-testid="tarjeta-tarea"
      data-tarea-id={tarea.id}
      onClick={() => onAbrir(tarea)}
      disabled={disabled}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: 12,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadiusLG,
        background: token.colorBgContainer,
        color: token.colorText,
        cursor: disabled ? 'default' : 'pointer',
        font: 'inherit',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {padre ? (
            <div
              style={{
                fontSize: 11,
                color: token.colorTextTertiary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              en: {padre.description}
            </div>
          ) : null}
          <div style={{ fontWeight: 500, wordBreak: 'break-word' }}>{tarea.description}</div>
        </div>
        {!disabled ? <RightOutlined style={{ color: token.colorTextTertiary }} /> : null}
      </div>

      <div style={{ marginTop: 4, fontSize: 12, color: token.colorTextSecondary }}>
        {nombreResponsable ?? 'Sin asignar'}
        {tarea.planned_start && tarea.planned_end
          ? ` · ${fechaCorta(tarea.planned_start)} → ${fechaCorta(tarea.planned_end)}`
          : ''}
        {dur ? ` · ${dur}` : ''}
      </div>

      {barra ? (
        <div
          style={{
            position: 'relative',
            height: 6,
            marginTop: 8,
            borderRadius: 3,
            background: token.colorFillSecondary,
          }}
        >
          <div
            data-testid="mini-barra"
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${barra.left}%`,
              width: `${barra.width}%`,
              borderRadius: 3,
              background: color,
            }}
          />
        </div>
      ) : null}

      {avisos.length > 0 ? (
        <div style={{ marginTop: 6, fontSize: 12, color: token.colorError }}>
          <WarningFilled /> {avisos.join(' · ')}
        </div>
      ) : null}
    </button>
  );
}

// Drawer de edición: fechas y responsable, un solo guardado.
function DrawerEdicion({ tarea, especialistas, onCerrar, onGuardar }) {
  const [inicio, setInicio] = useState(null);
  const [fin, setFin] = useState(null);
  const [responsable, setResponsable] = useState(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!tarea) return;
    setInicio(tarea.planned_start ? dayjs(tarea.planned_start) : null);
    setFin(tarea.planned_end ? dayjs(tarea.planned_end) : null);
    setResponsable(tarea.assigned_to_user_id ?? null);
  }, [tarea]);

  const guardar = async () => {
    if (inicio && fin && fin.isBefore(inicio, 'day')) {
      message.warning('La fecha de fin no puede ser anterior a la de inicio.');
      return;
    }
    setGuardando(true);
    const ok = await onGuardar(tarea.id, {
      planned_start: inicio ? inicio.format('YYYY-MM-DD') : null,
      planned_end: fin ? fin.format('YYYY-MM-DD') : null,
      assigned_to_user_id: responsable ?? null,
    });
    setGuardando(false);
    if (ok) onCerrar();
  };

  return (
    <Drawer
      open={Boolean(tarea)}
      onClose={onCerrar}
      placement="bottom"
      height="auto"
      title={tarea?.description}
      destroyOnClose
      footer={
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={onCerrar}>Cancelar</Button>
          <Button type="primary" loading={guardando} onClick={guardar}>
            Guardar
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <label style={{ display: 'block' }}>
          <div style={{ marginBottom: 4 }}>Inicio</div>
          <DatePicker
            aria-label="Inicio"
            format="DD/MM/YYYY"
            value={inicio}
            onChange={setInicio}
            style={{ width: '100%' }}
          />
        </label>
        <label style={{ display: 'block' }}>
          <div style={{ marginBottom: 4 }}>Fin</div>
          <DatePicker
            aria-label="Fin"
            format="DD/MM/YYYY"
            value={fin}
            onChange={setFin}
            style={{ width: '100%' }}
          />
        </label>
        <div>
          <div style={{ marginBottom: 4 }}>Responsable</div>
          <Select
            aria-label="Responsable"
            value={responsable ?? undefined}
            allowClear
            showSearch
            placeholder="Sin asignar"
            optionFilterProp="label"
            style={{ width: '100%' }}
            onChange={(next) => setResponsable(next ?? null)}
            options={especialistas.map((u) => ({ value: u.id, label: `${u.fullname}` }))}
          />
        </div>
      </Space>
    </Drawer>
  );
}

// Línea de tiempo de solo lectura: las barras sin lista a la izquierda ni flechas.
function LineaDeTiempo({ tareas, fechaComprometida, hoy }) {
  const scrollRef = useRef(null);
  const visibles = useMemo(() => tareasConBarra(tareas), [tareas]);
  const rango = useMemo(
    () => rangoDelDiagrama(visibles, fechaComprometida, hoy),
    [visibles, fechaComprometida, hoy]
  );

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const col = columnaInicial({ hoy, desde: rango.desde, dias: rango.dias });
    el.scrollLeft = Math.max(0, col * COL_WIDTH_MOVIL - MARGEN_HOY);
  }, [hoy, rango.desde, rango.dias]);

  if (visibles.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Ninguna tarea tiene fechas." />;
  }

  return (
    <div
      ref={scrollRef}
      data-testid="linea-de-tiempo"
      style={{ overflow: 'auto', maxHeight: '70vh' }}
    >
      <GanttBars
        tareas={visibles}
        dependencias={[]}
        fechaComprometida={fechaComprometida}
        rango={rango}
        hoy={hoy}
        colWidth={COL_WIDTH_MOVIL}
        offsetIzquierdo={0}
        readOnly
      />
    </div>
  );
}

// Vista de celular de la carta Gantt. La rejilla de dos paneles no cabe en una pantalla
// angosta, así que la vista principal es una lista de tareas agrupada por urgencia
// (Atrasadas, Esta semana, Próximas, Sin fechas, Terminadas), con edición por Drawer, y un
// selector para ver la línea de tiempo en solo lectura. Sin arrastre ni dependencias.
//
// Props:
//   tareas, dependencias, fechaComprometida — datos del summary.
//   especialistas — [{ id, fullname }] para el responsable.
//   readOnly — ODT finalizada (las tarjetas no abren edición).
//   toolbar — acciones de la página (importar, agregar tarea…).
//   hoy — 'YYYY-MM-DD' (por defecto, el día actual); inyectable para tests.
//   onEditarItem(idItem, campos) — persiste; devuelve la respuesta del API.
export default function GanttMobileList({
  tareas = [],
  dependencias = [],
  fechaComprometida = null,
  especialistas = [],
  readOnly = false,
  toolbar = null,
  hoy: hoyProp,
  onEditarItem,
}) {
  const { token } = theme.useToken();
  const [vista, setVista] = useState('lista');
  const [editando, setEditando] = useState(null);
  const [verTerminadas, setVerTerminadas] = useState(false);

  const hoy = useMemo(() => hoyProp ?? dayjs().format('YYYY-MM-DD'), [hoyProp]);
  const grupos = useMemo(() => agruparTareasMovil(tareas, hoy), [tareas, hoy]);
  const rango = useMemo(
    () => rangoDelDiagrama(tareas, fechaComprometida, hoy),
    [tareas, fechaComprometida, hoy]
  );
  const responsables = useMemo(
    () => new Map(especialistas.map((u) => [u.id, u.fullname])),
    [especialistas]
  );

  const guardar = async (id, campos) => {
    const res = await onEditarItem?.(id, campos);
    if (res?.codeMessage) {
      message.error(res.message || 'No se pudo guardar la tarea');
      return false;
    }
    return true;
  };

  return (
    <div>
      {toolbar ? (
        <Space wrap style={{ marginBottom: 12 }}>
          {toolbar}
        </Space>
      ) : null}

      <Segmented
        block
        value={vista}
        onChange={setVista}
        options={[
          { label: 'Lista', value: 'lista' },
          { label: 'Línea de tiempo', value: 'linea' },
        ]}
        style={{ marginBottom: 12 }}
      />

      {vista === 'linea' ? (
        <LineaDeTiempo tareas={tareas} fechaComprometida={fechaComprometida} hoy={hoy} />
      ) : grupos.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="La ODT no tiene tareas." />
      ) : (
        grupos.map((g) => {
          const colapsada = g.key === 'terminadas' && !verTerminadas;
          return (
            <section key={g.key} data-testid={`grupo-${g.key}`} style={{ marginBottom: 16 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 8,
                  fontWeight: 600,
                  color: g.key === 'atrasadas' ? token.colorError : token.colorText,
                }}
              >
                <span>
                  {g.label} ({g.items.length})
                </span>
                {g.key === 'terminadas' ? (
                  <Button type="link" size="small" onClick={() => setVerTerminadas((v) => !v)}>
                    {verTerminadas ? 'Ocultar' : 'Mostrar'}
                  </Button>
                ) : null}
              </div>
              {colapsada ? null : (
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  {g.items.map(({ tarea, padre }) => (
                    <TarjetaTarea
                      key={tarea.id}
                      tarea={tarea}
                      padre={padre}
                      avisos={avisosDeTarea(tarea, tareas, dependencias, fechaComprometida)}
                      rango={rango}
                      nombreResponsable={responsables.get(tarea.assigned_to_user_id)}
                      disabled={readOnly}
                      onAbrir={setEditando}
                    />
                  ))}
                </Space>
              )}
            </section>
          );
        })
      )}

      <DrawerEdicion
        tarea={editando}
        especialistas={especialistas}
        onCerrar={() => setEditando(null)}
        onGuardar={guardar}
      />
    </div>
  );
}
